'use client';

import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GameController,
  SpinnerGap,
  CheckCircle,
  Sparkle,
  ArrowRight,
  Package,
  Scales,
  Fire,
  Barbell,
  Drop,
  Leaf,
  X,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { useQuery, useMutation } from '@tanstack/react-query';
import gsap from 'gsap';
import { cn } from '@/lib/utils';
import { useCartStore } from '@/stores/cart-store';
import {
  getRouletteConfig,
  spinRoulette,
  type RouletteProduct,
  type SpinResult,
} from '@/lib/api/roulette';
import { ThemedImage } from '@/components/ui/themed-image';
import { Button } from '@/components/ui/primitives/button';
import { toast } from '@/components/ui/feedback/toast';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';

// ========== CONSTANTS ==========

const CARD_WIDTH = 96;
const CARD_GAP = 8;
const CARD_TOTAL = CARD_WIDTH + CARD_GAP;

// ========== HELPERS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== ROULETTE BELT ==========

function RouletteBelt({
  products,
  beltSequence,
  winnerIndex,
  containerWidth,
  isSpinning,
  hasFinished,
  onSpinComplete,
}: {
  products: RouletteProduct[];
  beltSequence: number[];
  winnerIndex: number;
  containerWidth: number;
  isSpinning: boolean;
  hasFinished: boolean;
  onSpinComplete: () => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const productMap = useMemo(() => {
    const map = new Map<number, RouletteProduct>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const targetX = useMemo(() => {
    return winnerIndex * CARD_TOTAL - containerWidth / 2 + CARD_WIDTH / 2;
  }, [winnerIndex, containerWidth]);

  useEffect(() => {
    if (!isSpinning || !trackRef.current) return;
    gsap.set(trackRef.current, { x: 0 });
    tweenRef.current = gsap.to(trackRef.current, {
      x: -targetX,
      duration: 5,
      ease: 'power3.out',
      onComplete: onSpinComplete,
    });
    return () => {
      tweenRef.current?.kill();
    };
  }, [isSpinning, targetX, onSpinComplete]);

  // Idle drift
  useEffect(() => {
    if (isSpinning || hasFinished || !trackRef.current) return;
    const tween = gsap.to(trackRef.current, {
      x: -20,
      duration: 3,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
    });
    return () => {
      tween.kill();
      if (trackRef.current) gsap.set(trackRef.current, { x: 0 });
    };
  }, [isSpinning, hasFinished]);

  return (
    <div className="relative w-full overflow-hidden">
      {/* Center marker — pure CSS centering, no JS width dependency */}
      <div
        className="absolute z-20 pointer-events-none"
        style={{ top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)' }}
      >
        <div className="flex flex-col items-center h-full">
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: '9px solid transparent',
              borderRight: '9px solid transparent',
              borderTop: '11px solid var(--primary)',
              filter: 'drop-shadow(0 2px 4px rgba(123, 66, 255, 0.4))',
            }}
          />
          <div
            className="flex-1"
            style={{
              width: 2,
              background: 'var(--primary)',
              boxShadow: '0 0 6px rgba(123, 66, 255, 0.5), 0 0 12px rgba(123, 66, 255, 0.2)',
            }}
          />
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: '9px solid transparent',
              borderRight: '9px solid transparent',
              borderBottom: '11px solid var(--primary)',
              filter: 'drop-shadow(0 -2px 4px rgba(123, 66, 255, 0.4))',
            }}
          />
        </div>
      </div>

      {/* Edge fades — CSS mask instead of bg gradient (works on any background) */}
      <div
        className="absolute inset-y-0 left-0 z-10 pointer-events-none"
        style={{
          width: 48,
          background: isDark
            ? 'linear-gradient(to right, var(--surface), transparent)'
            : 'linear-gradient(to right, var(--bg-elevated), transparent)',
        }}
      />
      <div
        className="absolute inset-y-0 right-0 z-10 pointer-events-none"
        style={{
          width: 48,
          background: isDark
            ? 'linear-gradient(to left, var(--surface), transparent)'
            : 'linear-gradient(to left, var(--bg-elevated), transparent)',
        }}
      />

      {/* Track */}
      <div
        ref={trackRef}
        className="flex items-center py-3"
        style={{ gap: CARD_GAP, willChange: 'transform' }}
      >
        {beltSequence.map((productId, i) => {
          const product = productMap.get(productId);
          if (!product) return null;
          const isWinner = hasFinished && i === winnerIndex;

          return (
            <div
              key={`${productId}-${i}`}
              className={cn(
                'shrink-0 flex flex-col items-center gap-1.5 p-2 rounded-card border transition-all duration-300',
                // Карточка товара — исключение из уплощения (ответ 35 = 2),
                // объём остаётся. shadow-[var(--primary)]/20 убран: он не красил
                // тень фиолетовым, тень и была чёрной (проверено сборкой).
                isWinner
                  ? 'border-[var(--primary)] bg-[var(--primary-subtle)] shadow-deep scale-[1.06]'
                  : 'bg-[var(--surface-secondary)] border-[var(--border-default)]',
              )}
              style={{ width: CARD_WIDTH }}
            >
              <div
                className={cn(
                  'relative w-[72px] h-[72px] rounded-card overflow-hidden border',
                  isWinner
                    ? 'border-[var(--primary)]/30'
                    : 'border-[var(--border-default)] bg-[var(--surface)]',
                )}
              >
                {product.image_url ? (
                  <ThemedImage src={product.image_url} alt={product.name} fill className="object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[var(--surface)]">
                    <GameController className="w-6 h-6 text-[var(--text-tertiary)]" />
                  </div>
                )}
              </div>
              <p className="text-[10px] font-medium text-[var(--text-secondary)] text-center truncate w-full leading-tight">
                {product.name}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== PRODUCT CATALOG GRID ==========

// ========== PRODUCT DETAIL (read-only) ==========

function ProductDetail({
  product,
  onClose,
}: {
  product: ProductWithCategories;
  onClose: () => void;
}) {
  const hasNutrition = product.calories || product.proteins || product.fats || product.carbs;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="rounded-card border border-[var(--border-default)] bg-[var(--surface-secondary)] overflow-hidden"
    >
      {/* Image */}
      <div className="relative w-full aspect-[16/10] bg-[var(--surface)]">
        {product.image_url ? (
          <ThemedImage src={product.image_url} alt={product.name} fill className="object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-[var(--text-tertiary)]" />
          </div>
        )}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-pill bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        >
          <X className="w-4 h-4" weight="bold" />
        </button>
      </div>

      {/* Name + weight */}
      <div className="px-4 pt-4 pb-3">
        <h4 className="text-base font-bold text-[var(--text-primary)] leading-tight">
          {product.name}
        </h4>
        {product.weight && (
          <div className="flex items-center gap-1 mt-1">
            <Scales className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
            <span className="text-xs text-[var(--text-secondary)]">{product.weight} г</span>
          </div>
        )}
      </div>

      {/* Description */}
      {product.description && (
        <div className="px-4 pb-3">
          <p className="text-xs text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
            {product.description}
          </p>
        </div>
      )}

      {/* КБЖУ */}
      {hasNutrition && (
        <div className="mx-4 mb-3 rounded-card bg-[var(--surface)] border border-[var(--border-default)] p-3 space-y-2">
          {[
            { label: 'Калории', value: product.calories, unit: 'ккал', icon: Fire },
            { label: 'Белки', value: product.proteins, unit: 'г', icon: Barbell },
            { label: 'Жиры', value: product.fats, unit: 'г', icon: Drop },
            { label: 'Углеводы', value: product.carbs, unit: 'г', icon: Leaf },
          ].filter((item) => item.value != null).map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <span className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
                <item.icon weight="fill" className="w-4 h-4 text-[var(--primary)]" />
                {item.label}
              </span>
              <span className="text-xs font-semibold text-[var(--text-primary)]">
                {item.value} {item.unit}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Состав */}
      {product.ingredients && (
        <div className="mx-4 mb-4 rounded-card bg-[var(--surface)] border border-[var(--border-default)] p-3">
          <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider mb-1.5">
            Состав
          </p>
          <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
            {Array.isArray(product.ingredients) ? product.ingredients.join(', ') : product.ingredients}
          </p>
        </div>
      )}
    </motion.div>
  );
}

// ========== PRODUCT CATALOG GRID ==========

function ProductCatalog({ products }: { products: RouletteProduct[] }) {
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [detailProduct, setDetailProduct] = useState<ProductWithCategories | null>(null);

  const handleProductClick = useCallback(async (productId: number) => {
    if (loadingId) return;

    // Toggle off if same product
    if (detailProduct?.id === productId) {
      setDetailProduct(null);
      return;
    }

    setLoadingId(productId);
    try {
      const fullProduct = await getProductById(productId);
      setDetailProduct(fullProduct);
    } catch {
      toast.error('Не удалось загрузить товар');
    } finally {
      setLoadingId(null);
    }
  }, [loadingId, detailProduct]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
        Товары в рулетке
      </p>

      {/* Detail view */}
      <AnimatePresence>
        {detailProduct && (
          <ProductDetail
            key={detailProduct.id}
            product={detailProduct}
            onClose={() => setDetailProduct(null)}
          />
        )}
      </AnimatePresence>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-2">
        {products.map((product, idx) => {
          const isActive = detailProduct?.id === product.id;

          return (
            <motion.button
              key={product.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              onClick={() => handleProductClick(product.id)}
              disabled={loadingId === product.id}
              className={cn(
                'flex gap-2.5 p-2.5 rounded-card border text-left',
                'transition-all duration-150 active:scale-[0.97]',
                isActive
                  ? 'border-[var(--primary)] bg-[var(--primary-subtle)]'
                  : 'border-[var(--border-default)] bg-[var(--surface-secondary)] hover:border-[var(--primary)] hover:bg-[var(--primary-subtle)]',
              )}
            >
              <div className="relative w-12 h-12 rounded-card overflow-hidden border border-[var(--border-default)] shrink-0 bg-[var(--surface)]">
                {loadingId === product.id ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <SpinnerGap className="w-5 h-5 text-[var(--primary)] animate-spin" />
                  </div>
                ) : product.image_url ? (
                  <ThemedImage src={product.image_url} alt={product.name} fill className="object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-[var(--text-tertiary)]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <p className="text-xs font-semibold text-[var(--text-primary)] truncate leading-tight">
                  {product.name}
                </p>
                {product.weight && (
                  <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{product.weight}</p>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ========== WIN OVERLAY ==========

function WinOverlay({
  winner,
  spinPrice,
  onClose,
}: {
  winner: RouletteProduct;
  spinPrice: number;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col px-4 pt-2 pb-4"
    >
      {/* Product hero card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 25 }}
        className="rounded-card border border-[var(--border-default)] bg-[var(--surface-secondary)] overflow-hidden mb-4"
      >
        {/* Image area */}
        <div className="relative w-full aspect-[16/10] bg-[var(--surface)]">
          {winner.image_url ? (
            <ThemedImage
              src={winner.image_url}
              alt={winner.name}
              fill
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkle className="w-12 h-12 text-[var(--text-tertiary)]" weight="fill" />
            </div>
          )}

          {/* Price badge — top right */}
          <div className="absolute top-3 right-3">
            <div className="px-3 py-1.5 rounded-pill bg-[var(--primary)] shadow-float">
              <span className="text-sm font-bold font-mono text-white">{formatPrice(spinPrice)}</span>
            </div>
          </div>
        </div>

        {/* Product details */}
        <div className="p-4">
          <h3 className="text-base font-bold text-[var(--text-primary)] mb-1 leading-tight">
            {winner.name}
          </h3>

          {winner.description && (
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line mb-3 line-clamp-3">
              {winner.description}
            </p>
          )}

          {/* Meta row */}
          {winner.weight && (
            <div className="flex items-center gap-4 pt-3 border-t border-[var(--border-default)]">
              <div className="flex items-center gap-1.5">
                <Scales className="w-4 h-4 text-[var(--text-tertiary)]" />
                <span className="text-xs text-[var(--text-secondary)]">Масса</span>
              </div>
              <span className="text-xs font-semibold text-[var(--text-primary)]">{winner.weight}</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Continue button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button
          variant="default"
          size="xl"
          className="w-full"
          onClick={onClose}
          rightIcon={<ArrowRight className="w-5 h-5" />}
        >
          Продолжить заказ
        </Button>
      </motion.div>
    </motion.div>
  );
}

// ========== MAIN COMPONENT ==========

interface RouletteStepProps {
  onNext?: () => void;
}

export function RouletteStep({ onNext }: RouletteStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const containerRef = useRef<HTMLDivElement>(null);
  const beltContainerRef = useRef<HTMLDivElement>(null);
  const [beltWidth, setBeltWidth] = useState(360);
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const [spinResult, setSpinResult] = useState<SpinResult | null>(null);
  const [showWin, setShowWin] = useState(false);
  const [previewSequence, setPreviewSequence] = useState<number[]>([]);

  const hasRouletteItem = useCartStore((s) => s.hasRouletteItem());
  const addRouletteItem = useCartStore((s) => s.addRouletteItem);

  const { data: config, isLoading } = useQuery({
    queryKey: ['roulette-config'],
    queryFn: getRouletteConfig,
    staleTime: 60000,
  });

  const spinPrice = config?.price ?? 299;
  const isActive = config?.is_active ?? false;
  const products = config?.products ?? [];

  useEffect(() => {
    if (products.length > 0) {
      const sequence: number[] = [];
      for (let i = 0; i < 50; i++) {
        sequence.push(products[i % products.length].id);
      }
      setPreviewSequence(sequence);
    }
  }, [products]);

  // Measure belt container
  useEffect(() => {
    if (!beltContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setBeltWidth(entry.contentRect.width);
    });
    observer.observe(beltContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const spinMutation = useMutation({
    mutationFn: spinRoulette,
    onSuccess: (result) => {
      setSpinResult(result);
      setIsSpinning(true);
      setHasFinished(false);
    },
    onError: (error: unknown) => {
      setIsSpinning(false);
      // «Сет дня» не суммируется с рулеткой — сервер не даёт крутить.
      // Показываем причину, иначе кнопка просто «не срабатывает».
      const message = (error as { response?: { data?: { error?: string; error_code?: string } } })
        ?.response?.data;
      if (message?.error_code === 'roulette_with_set_of_the_day') {
        toast.error(message.error || '«Сет дня» не суммируется с рулеткой и роллом за первый заказ.');
      }
    },
  });

  const handleSpin = useCallback(() => {
    if (isSpinning || spinMutation.isPending || hasRouletteItem) return;
    // Шлём состав корзины: сервер сам проверит по БД, нет ли там «Сета дня».
    const cartProductIds = useCartStore
      .getState()
      .items.map((i) => Number(i.id))
      .filter(Number.isInteger);
    spinMutation.mutate(cartProductIds);
  }, [isSpinning, spinMutation, hasRouletteItem]);

  const handleSpinComplete = useCallback(() => {
    if (!spinResult) return;
    setHasFinished(true);

    setTimeout(() => {
      const winner = spinResult.winner;
      addRouletteItem(
        {
          id: winner.id,
          name: winner.name,
          price: winner.price,
          image_url: winner.image_url,
          sku: winner.sku || '',
        } as any,
        spinPrice,
      );
      toast.success('Добавлено в корзину', {
        description: `${winner.name} за ${formatPrice(spinPrice)}`,
      });
      setIsSpinning(false);
      setShowWin(true);
    }, 800);
  }, [spinResult, addRouletteItem, spinPrice]);

  const handleWinClose = useCallback(() => {
    setShowWin(false);
    onNext?.();
  }, [onNext]);

  // ===== INACTIVE =====
  if (!isActive || products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-card bg-[var(--surface-tertiary)] flex items-center justify-center mb-4">
          <GameController className="w-8 h-8 text-[var(--text-disabled)]" />
        </div>
        <p className="text-sm text-[var(--text-tertiary)] text-center mb-5">
          Рулетка сейчас недоступна
        </p>
        <Button variant="outline" size="lg" onClick={onNext} rightIcon={<ArrowRight />}>
          Пропустить
        </Button>
      </div>
    );
  }

  // ===== LOADING =====
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-10 h-10 rounded-pill bg-[var(--primary-subtle)] flex items-center justify-center">
          <SpinnerGap className="w-5 h-5 text-[var(--primary)] animate-spin" />
        </div>
        <p className="text-xs text-[var(--text-tertiary)]">Загрузка...</p>
      </div>
    );
  }

  // ===== ALREADY USED =====
  if (hasRouletteItem && !showWin) {
    return (
      <div className="flex flex-col items-center justify-center py-14 px-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="w-16 h-16 rounded-card bg-[var(--success)]/10 flex items-center justify-center mb-4"
        >
          <CheckCircle className="w-8 h-8 text-[var(--success)]" weight="fill" />
        </motion.div>
        <p className="text-base font-bold text-[var(--text-primary)] mb-1">Рулетка использована</p>
        <p className="text-sm text-[var(--text-secondary)] text-center mb-5">
          Выигрыш уже в корзине
        </p>
        <Button variant="default" size="lg" onClick={onNext} rightIcon={<ArrowRight />}>
          Продолжить
        </Button>
      </div>
    );
  }

  // ===== MAIN VIEW =====
  return (
    <div ref={containerRef} className="flex flex-col">
      <AnimatePresence mode="wait">
        {showWin && spinResult ? (
          <WinOverlay
            key="win"
            winner={spinResult.winner}
            spinPrice={spinPrice}
            onClose={handleWinClose}
          />
        ) : (
          <motion.div
            key="roulette"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="flex flex-col"
          >
            {/* Belt — full bleed */}
            <div
              ref={beltContainerRef}
              className={cn(
                'border-y mb-4',
                isDark
                  ? 'bg-[var(--surface)] border-[var(--border-default)]'
                  : 'bg-[var(--bg-elevated)] border-[var(--border-default)]',
              )}
            >
              <RouletteBelt
                products={products}
                beltSequence={isSpinning && spinResult ? spinResult.belt_sequence : previewSequence}
                winnerIndex={isSpinning && spinResult ? spinResult.winner_index : 0}
                containerWidth={beltWidth}
                isSpinning={isSpinning}
                hasFinished={hasFinished}
                onSpinComplete={handleSpinComplete}
              />
            </div>

            {/* Buttons — stacked */}
            <div className="px-4 flex flex-col gap-2 mb-4">
              <button
                onClick={handleSpin}
                disabled={isSpinning || spinMutation.isPending || hasRouletteItem}
                className={cn(
                  'w-full h-12 rounded-card font-medium text-sm flex items-center justify-center gap-2',
                  'transition-[background-color,opacity,box-shadow] duration-100 active:scale-[0.98]',
                  isSpinning || spinMutation.isPending || hasRouletteItem
                    ? 'bg-[var(--surface-disabled)] text-[var(--text-disabled)]'
                    : 'bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)] hover:shadow-float',
                )}
              >
                {spinMutation.isPending ? (
                  <SpinnerGap className="w-5 h-5 animate-spin" />
                ) : (
                  <GameController className="w-5 h-5" weight="fill" />
                )}
                {spinMutation.isPending
                  ? 'Подготовка...'
                  : isSpinning
                    ? 'Крутим...'
                    : `Крутить за ${formatPrice(spinPrice)}`}
              </button>

              {!isSpinning && (
                <button
                  onClick={onNext}
                  className={cn(
                    'w-full h-12 rounded-card border font-medium text-sm flex items-center justify-center',
                    'transition-[background-color,opacity] duration-100 active:scale-[0.98] active:transition-none',
                    'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primary-subtle)]',
                  )}
                >
                  Пропустить
                </button>
              )}
            </div>

            {/* Error */}
            {spinMutation.isError && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-4 mb-4 px-4 py-2.5 rounded-card bg-[var(--error)]/10 border border-[var(--error)]/20 text-center"
              >
                <p className="text-sm text-[var(--error)]">
                  Не удалось запустить. Попробуйте ещё раз.
                </p>
              </motion.div>
            )}

            {/* Product catalog */}
            {!isSpinning && products.length > 0 && (
              <div className="px-4 pb-4">
                <ProductCatalog products={products} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
